import React from 'react';
import styled, { css } from 'styled-components';
// Untyped .js import -> resolves as any.
import DownArrow from 'icons/down-arrow';

import { gridUnits } from '../utils';
import { widths, typeConsts } from './constants';

const BORDER_RADIUS = '4px';
const BORDER_WIDTH = '1px';
const PADDING = '10px';
// Numeric so template-string arithmetic (TRANSITION_DURATION / 2) type-checks;
// emitted CSS strings are identical to the previous string literal '200'.
const TRANSITION_DURATION = 200;
const MAX_DROPDOWN_HEIGHT = 160;
const SHADOW_BUFFER = 5;

interface ContainerProps {
  width?: string | number;
  primaryColor?: string;
  disabled?: boolean;
}

interface FascadeProps {
  textColor?: string;
  selectedValueColor?: string;
}

interface FormSelectInputProps {
  primaryColor?: string;
}

interface IconProps {
  noBorder?: boolean;
  bgColorIcon?: string;
  type?: string;
  // Always a CSS length string (iconSize -> gridUnits returns `${n}px`); matches DownArrow's size prop.
  size?: string;
}

interface BorderProps {
  type?: string;
  noBorder?: boolean;
  bgColor?: string;
  alignRight?: boolean;
  closing?: boolean;
  open?: boolean;
  outlineOnFocus?: boolean;
  primaryColor?: string;
}

interface DropdownContainerProps {
  type?: string;
  open?: boolean;
  closing?: boolean;
  innerHeight?: number;
  noBorder?: boolean;
}

interface DropdownProps {
  type?: string;
  noBorder?: boolean;
  innerHeight?: number;
  dropdownBgColor?: string;
  open?: boolean;
  closing?: boolean;
}

interface OptionListProps {
  alignRight?: boolean;
}

interface OptionProps {
  type?: string;
  textColor?: string;
  primaryColor?: string;
  selected?: boolean;
  highlighted?: boolean;
  disabledOptionColor?: string;
  selectedBgColor?: string;
  selectedOptionColor?: string;
  dropdownBgColor?: string;
}

const getWidth = ({ width }: ContainerProps): string => {
  if (!width) return 'auto';
  return widths[width] || `${width}px`;
};

export const Container = styled.div<ContainerProps>`
  position: relative;
  display: inline-flex;
  font-size: 12px;
  width: ${getWidth};
  color: ${(props) => props.primaryColor};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

export const Fascade = styled.div<FascadeProps>`
  display: flex;
  pointer-events: none;
  align-items: center;
  flex: 1;
  color: ${(props) => props.textColor || props.selectedValueColor || 'inherit'};
`;

export const Label = styled.span`
  flex: 1;
  font-size: inherit;
  font-weight: 400;
  margin: 0;
  padding: 0;
  line-height: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FormSelectInput = styled.input.attrs({ type: 'text' })<FormSelectInputProps>`
  flex: 1;
  font-size: inherit;
  font-weight: 400;
  font-size: inherit;
  color: ${({ primaryColor }) => primaryColor};
  border: none !important;
  outline: none !important;
  background: transparent;
`;

const iconSize = ({ type }: { type?: string }): string => {
  if (type === typeConsts.LG) return gridUnits(1.5);
  return gridUnits(1);
};

const iconRadius = (props: IconProps): string | number => {
  if (props.noBorder || props.type === typeConsts.FORM) {
    return BORDER_RADIUS;
  }
  return 0;
};

const IconInner = ({
  noBorder,
  bgColorIcon,
  ...attrs
}: IconProps): React.ReactElement => <DownArrow {...attrs} />;

export const Icon = styled(IconInner).attrs<IconProps>((props) => ({
  style: {
    primaryColor: '#FFFFFF',
  },
  size: iconSize(props),
}))`
  background-color: ${(props) => props.bgColorIcon};
  margin: ${(props) => (props.noBorder ? 0 : `-${BORDER_WIDTH}`)};
  margin-left: ${PADDING};
  border-radius: ${iconRadius};
`;

export const Select = styled.select`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
`;

const borderPadding = ({ type }: { type?: string }): string => {
  if (type === typeConsts.FORM) return '10px 7px';
  return `0 0 0 ${PADDING}`;
};

const borderRadius = ({ type }: { type?: string }): string => {
  if (type === typeConsts.FORM) return '0';
  return `${BORDER_RADIUS}`;
};

export const Border = styled.div<BorderProps>`
  flex: 1;
  padding: ${borderPadding};
  overflow: hidden;
  background-color: ${(props) => props.bgColor};
  text-align: ${({ alignRight }) => (alignRight ? 'right' : 'left')};
  border: ${(props) => (props.noBorder ? 0 : BORDER_WIDTH)} solid currentColor;
  border-radius: ${borderRadius};
  will-change: border-radius;
  transform: translateZ(0);
  transition: border-radius ${TRANSITION_DURATION / 2}ms ease-in-out
    ${(props) => (props.closing ? `${TRANSITION_DURATION / 2}ms` : '0ms')};
  ${(props) =>
    props.open &&
    !props.noBorder &&
    `
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `};

  &:focus-within {
    outline: ${({ outlineOnFocus, primaryColor }) =>
      outlineOnFocus ? `3px auto ${primaryColor}` : 'none'};
  }
`;

export const DropdownContainer = styled.div<DropdownContainerProps>`
  position: absolute;
  top: 100%;
  left: -${SHADOW_BUFFER}px;
  right: -${SHADOW_BUFFER}px;
  height: ${(props) => (props.open || props.closing ? props.innerHeight! + 5 : 0)}px;
  max-height: ${MAX_DROPDOWN_HEIGHT + SHADOW_BUFFER}px;
  border-bottom-left-radius: ${borderRadius};
  border-bottom-right-radius: ${borderRadius};
  overflow: hidden;
  ${(props) => props.noBorder && `margin-top: 4px`}};
`;

// Dropdown
const dropdownOpen = css`
  transform: translate3d(0, 0, 0);
  overflow-y: scroll;
  overflow-x: hidden;
`;

const dropdownBorder = css<DropdownProps>`
  border-width: ${(props) => (props.noBorder ? 0 : `0 ${BORDER_WIDTH} ${BORDER_WIDTH}`)};
  ${'' /* box-shadow: 4px 0px 14px 0 rgba(0, 0, 0, 0.4); */}
  border-bottom-left-radius: ${borderRadius};
  border-bottom-right-radius: ${borderRadius};
  z-index: 10;
`;

export const Dropdown = styled.div<DropdownProps>`
  position: absolute;
  top: 0;
  left: ${(props) => (props.noBorder ? '15px' : `${SHADOW_BUFFER}px`)};
  right: ${SHADOW_BUFFER}px;
  transform: translate3d(0, -100%, 0);
  max-height: ${MAX_DROPDOWN_HEIGHT}px;
  height: ${(props) => props.innerHeight}px;
  border: 0 solid currentColor;
  border-radius: inherit;
  overflow: hidden;
  will-change: transform;
  transition: transform ${TRANSITION_DURATION}ms ease-in-out;
  background-color: ${(props) => props.dropdownBgColor};
  box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.3);
  ${(props) => props.open && dropdownOpen};
  ${(props) => (props.open || props.closing) && dropdownBorder};
`;

export const OptionList = styled.ul<OptionListProps>`
  position: relative;
  padding: ${({ alignRight }) => (alignRight ? '0 20px 0 0' : 0)};
  margin: 0;
  display: flex;
  flex-direction: column;
  text-align: ${({ alignRight }) => (alignRight ? 'right' : 'left')};
`;

// Option
const optionSelected = css<OptionProps>`
  color: ${(props) => props.disabledOptionColor};
`;

const optionHighlighted = css<OptionProps>`
  background-color: ${(props) => props.selectedBgColor || props.primaryColor};
  color: ${(props) => props.textColor || props.selectedOptionColor || props.dropdownBgColor};
`;

const optionPaddingVertical = ({ type }: { type?: string }): string => {
  if (type === typeConsts.FORM || type === typeConsts.LG) {
    return PADDING;
  }
  return `${parseInt(PADDING, 10) / 2}px`;
};

export const Option = styled.li<OptionProps>`
  position: relative;
  padding: ${optionPaddingVertical} ${PADDING};
  display: block;
  cursor: default;
  color: ${(props) => props.textColor || props.primaryColor};
  ${(props) => (props.selected && optionSelected) || (props.highlighted && optionHighlighted)};
`;
